import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { UpdateStaffTicketRequest } from "@signalist/shared-types";

import { TicketAttachmentChip } from "@/pages/Private/pages/Support/pages/TicketDetail/components/TicketAttachmentChip";
import { allTicketAttachments } from "@/pages/Private/pages/Support/pages/TicketDetail/utils/ticketThread";

import { StaffTicketDetailController } from "../hooks/staffTicketDetailController";
import { TriageOutcome } from "../hooks/useTicketTriage";
import { OtherCompanyTickets } from "./OtherCompanyTickets";
import { RailHeading } from "./RailSection";
import { TicketContextSection } from "./TicketContextSection";
import { TicketCustomerCard } from "./TicketCustomerCard";
import { TicketTriageSection } from "./TicketTriageSection";

interface StaffTicketRailProps {
	controller: StaffTicketDetailController;
	onTriageOutcome: (outcome: TriageOutcome, fields: UpdateStaffTicketRequest) => void;
	onLoginAs: () => void;
	onAttachmentError: (fileName: string) => void;
}

/** The mockup's `.rail`: Triage, Customer, Context, Attachments · N, Other tickets from <company>. */
export const StaffTicketRail: FunctionComponent<StaffTicketRailProps> = ({
	controller,
	onTriageOutcome,
	onLoginAs,
	onAttachmentError,
}) => {
	const { t } = useTranslation();
	const { ticket } = controller;
	const attachments = allTicketAttachments(ticket);

	return (
		<aside
			className="flex w-[280px] shrink-0 flex-col overflow-y-auto text-xs"
			data-testid="staff-rail"
		>
			<TicketTriageSection
				assignees={controller.assignees}
				isTriaging={controller.isTriaging}
				ticket={ticket}
				triage={controller.triage}
				onOutcome={onTriageOutcome}
			/>
			<TicketCustomerCard
				company={controller.company}
				isLoggingIn={controller.isLoggingIn}
				openTicketCount={controller.openTicketCount}
				supportUser={controller.supportUser}
				ticket={ticket}
				onLoginAs={onLoginAs}
			/>
			<TicketContextSection ticket={ticket} />

			<RailHeading>
				{t("support.staff.detail.rail.attachments", { count: attachments.length })}
			</RailHeading>
			<div className="mt-1.5 flex flex-col items-start gap-1.5">
				{attachments.length === 0 ? (
					<span className="text-text-meta">{t("support.staff.detail.rail.noAttachments")}</span>
				) : (
					attachments.map((attachment) => (
						<TicketAttachmentChip
							key={attachment.id}
							attachment={attachment}
							onError={onAttachmentError}
						/>
					))
				)}
			</div>

			<OtherCompanyTickets companyName={ticket.companyName} tickets={controller.otherTickets} />
		</aside>
	);
};
